import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import ProductCardSkeleton from "@/components/ProductCardSkeleton";
import { PRODUCTS, CATEGORIES, VIBES } from "@/data/products";
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import {
  SHOPIFY_CONFIGURED,
  fetchProducts,
  fetchProductsByCollection,
  fetchCollections,
} from "@/lib/shopify";

const SORT_OPTIONS = [
  { id: "new", label: "Newest" },
  { id: "price-low", label: "Price: Low to High" },
  { id: "price-high", label: "Price: High to Low" },
  { id: "popular", label: "Most Loved" },
];

/**
 * Maps the URL `category` param (used by header nav) to a Shopify collection
 * handle. Falls back to the param itself, then to a tag-based query if no
 * matching collection exists.
 */
const CATEGORY_TO_COLLECTION = {
  earrings: "earrings",
  necklaces: "necklace",       // Shopify handle is singular
  "luxury-necklaces": "luxury-necklaces",
  rings: "rings",
  bracelets: "bracelets",
  cuffs: "chunky-cuffs",
  ocean: "ocean-collection",
  sets: "combo-earrings-and-necklaces",
  gifts: "gifts",
  "under-499": "under-499",
  b1g1: "buy-1-get-1-free",
  b1g2: "buy-1-get-2-free",
};

// Additional candidate handles tried if the primary handle above isn't found
// in the live Shopify collections list. First match wins.
const CATEGORY_CANDIDATES = {
  b1g1: ["buy-1-get-1-free", "buy-1-get-1", "mini-box", "b1g1", "combo-earrings-and-necklaces"],
  b1g2: ["buy-1-get-2-free", "buy-1-get-2", "glam-box", "b1g2", "combo-earrings-and-necklaces"],
};

// Fallback: match by collection *name* if none of the handles above are found.
// Handles Shopify's auto-generated slug variations gracefully.
const CATEGORY_NAME_REGEX = {
  b1g1: /buy\s*1\s*get\s*1\s*free/i,
  b1g2: /buy\s*1\s*get\s*2\s*free/i,
};

// Categories that come from a dedicated banner CTA — hide the generic
// category-pills filter bar because the user has already landed on a
// curated collection page.
const HIDE_FILTER_BAR_FOR = new Set(["b1g1", "b1g2", "cuffs"]);

// Page-title overrides for banner pages (used when Shopify collection isn't
// found by handle — ensures the header never says "All Jewelry").
const CATEGORY_TITLE_OVERRIDE = {
  b1g1: { title: "Mini Box · B1G1", sub: "Buy 1 Get 1 Free · Pick any 2 pieces" },
  b1g2: { title: "Glam Box · B1G2", sub: "Buy 1 Get 2 Free · Pick any 3 pieces" },
  cuffs: { title: "Chunky Cuffs", sub: "Bold · Sculptural · Statement" },
};

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category");
  const vibe = searchParams.get("vibe");
  const filter = searchParams.get("filter");

  const [sort, setSort] = useState("new");
  const [sortOpen, setSortOpen] = useState(false);

  /* ----------------- Live Shopify data ----------------- */
  const [liveProducts, setLiveProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(SHOPIFY_CONFIGURED);
  const [error, setError] = useState(null);

  // Load Shopify collection list once (used to detect valid handles)
  useEffect(() => {
    if (!SHOPIFY_CONFIGURED) return;
    fetchCollections({ first: 30 })
      .then(setCollections)
      .catch(() => {});
  }, []);

  // Legacy redirects — the B1G1/B1G2 model is replaced by fixed-price bundles
  const navigate = useNavigate();
  useEffect(() => {
    if (category === "b1g1") navigate("/bundle/it-girl", { replace: true });
    else if (category === "b1g2") navigate("/bundle/glam", { replace: true });
  }, [category, navigate]);

  // Fetch products based on category / filter
  useEffect(() => {
    if (!SHOPIFY_CONFIGURED) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const primaryHandle =
      category && (CATEGORY_TO_COLLECTION[category] || category);
    const candidates = CATEGORY_CANDIDATES[category] || (primaryHandle ? [primaryHandle] : []);
    // Pick the first candidate that actually exists in the live collections list
    let validHandle =
      collections.length > 0
        ? candidates.find((h) => collections.some((c) => c.id === h)) || null
        : null;
    // Fallback: match by collection *name* (case-insensitive regex).
    if (!validHandle && collections.length > 0 && CATEGORY_NAME_REGEX[category]) {
      const nameRe = CATEGORY_NAME_REGEX[category];
      const byName = collections.find((c) => nameRe.test(c.name || ""));
      if (byName) validHandle = byName.id;
    }

    let promise;
    if (validHandle) {
      promise = fetchProductsByCollection(validHandle, { first: 50 });
    } else if (filter === "new") {
      promise = fetchProducts({ first: 50, query: "tag:new" });
    } else if (category) {
      // No matching collection — try tag-based fallback
      promise = fetchProducts({ first: 50, query: `tag:${category}` });
    } else {
      promise = fetchProducts({ first: 100 });
    }

    promise
      .then((list) => {
        if (cancelled) return;
        const cleaned = list.filter(Boolean);
        // Stamp bogoTier onto products when they came from a BOGO collection page.
        // This makes the cart's tier math collection-isolated even if Shopify
        // products don't carry the explicit tag.
        // Chunky Cuffs is also a Buy-1-Get-1-Free collection.
        const bogoStamp =
          category === "b1g1" || category === "cuffs" ? "b1g1"
            : category === "b1g2" ? "b1g2"
            : null;
        const stamped = bogoStamp
          ? cleaned.map((p) => ({ ...p, bogoTier: bogoStamp }))
          : cleaned;
        setLiveProducts(stamped);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [category, filter, collections]);

  /* ----------------- Sort + final list ----------------- */
  const showLive = SHOPIFY_CONFIGURED && !error;

  const products = useMemo(() => {
    let list = showLive ? [...liveProducts] : [...PRODUCTS];

    if (!showLive) {
      if (category) list = list.filter((p) => p.category === category);
      if (vibe) list = list.filter((p) => p.vibe === vibe);
      if (filter === "new")
        list = list.filter((p) => p.badge === "New" || p.badge === "Hot");
    } else if (vibe) {
      // vibe is a mock-only concept (no Shopify mapping yet)
      list = list.filter((p) => p.vibe === vibe);
    }

    switch (sort) {
      case "price-low":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        list.sort((a, b) => b.price - a.price);
        break;
      case "popular":
        list.sort(
          (a, b) => (b.isBestseller ? 1 : 0) - (a.isBestseller ? 1 : 0),
        );
        break;
      default:
        break;
    }
    return list;
  }, [showLive, liveProducts, category, vibe, filter, sort]);

  /* ----------------- Page chrome ----------------- */
  const activeCat = CATEGORIES.find((c) => c.id === category);
  const activeVibe = VIBES.find((v) => v.id === vibe);
  const activeShopifyCollection = collections.find(
    (c) => c.id === (CATEGORY_TO_COLLECTION[category] || category),
  );
  const hideFilterBar = HIDE_FILTER_BAR_FOR.has(category);
  const titleOverride = CATEGORY_TITLE_OVERRIDE[category];
  const pageTitle =
    titleOverride?.title ||
    activeShopifyCollection?.name ||
    (activeCat ? activeCat.name : null) ||
    (activeVibe ? activeVibe.name : null) ||
    (filter === "new" ? "New Arrivals" : "All Jewelry");
  const pageSubtitle =
    titleOverride?.sub ||
    activeShopifyCollection?.description ||
    (activeCat ? activeCat.sub : null) ||
    (activeVibe ? activeVibe.descriptor : null) ||
    "Every piece, every story";

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div data-testid="shop-page">
      {/* Page header */}
      <section className="pt-12 lg:pt-20 pb-10 lg:pb-14" style={{ background: "#FBEAF0" }}>
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="gold-divider" />
            <span className="overline">Shop</span>
            <span className="gold-divider" />
          </div>
          <h1 className="font-serif text-plum leading-[1] tracking-tight" style={{ fontSize: "clamp(38px, 5vw, 72px)" }}>
            {pageTitle}
          </h1>
          <p className="mt-4 text-plum/70 max-w-[460px] mx-auto text-[15px]">{pageSubtitle}</p>
        </div>
      </section>

      {/* Chunky Cuffs — landscape campaign banner (B1G1) */}
      {category === "cuffs" && (
        <section
          className="w-full bg-[#FDF6F0] py-8 lg:py-12"
          data-testid="chunky-cuffs-collection-banner"
        >
          <div className="max-w-[1440px] mx-auto px-4 lg:px-10">
            <a
              href="#products"
              className="group block relative overflow-hidden shadow-[0_30px_60px_-30px_rgba(74,44,53,0.4)]"
              aria-label="Buy 1 Get 1 Free — Statement Cuffs"
            >
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_grove-design/artifacts/3cll1jos_WhatsApp%20Image%202026-06-15%20at%2022.33.51.jpeg"
                alt="Buy 1 Get 1 Free — Statement Cuffs · Limited Edition Luxe Cuff"
                loading="eager"
                className="w-full h-auto block transition-transform duration-[1600ms] ease-out group-hover:scale-[1.015]"
              />
              {/* subtle gold sweep on hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{
                  background:
                    "linear-gradient(115deg, transparent 42%, rgba(255,236,180,0.28) 50%, transparent 58%)",
                }}
              />
            </a>
          </div>
        </section>
      )}

      <div id="products" />

      {/* Filter & Sort bar — hidden on curated banner pages (B1G1 / B1G2 / Chunky Cuff) */}
      <div className={`border-b border-sectionbreak sticky top-[120px] lg:top-[128px] z-30 backdrop-blur-xl bg-cream/85 ${hideFilterBar ? "hidden" : ""}`}>
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar">
            <button
              onClick={() => { setParam("category", ""); setParam("vibe", ""); setParam("filter", ""); }}
              className={`text-[12px] tracking-[0.14em] uppercase whitespace-nowrap px-4 py-2 rounded-full border transition-colors ${
                !category && !vibe && !filter ? "bg-plum text-cream border-plum" : "border-gold/30 text-plum/70 hover:border-gold"
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            {CATEGORIES.slice(0, 6).map((c) => (
              <button
                key={c.id}
                onClick={() => setParam("category", c.id === category ? "" : c.id)}
                className={`text-[12px] tracking-[0.14em] uppercase whitespace-nowrap px-4 py-2 rounded-full border transition-colors ${
                  category === c.id ? "bg-plum text-cream border-plum" : "border-gold/30 text-plum/70 hover:border-gold"
                }`}
                data-testid={`filter-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="relative flex-shrink-0">
            <button
              onClick={() => setSortOpen((v) => !v)}
              className="flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-plum px-3 py-2 border border-gold/30 rounded-full hover:border-gold"
              data-testid="sort-button"
            >
              <SlidersHorizontal size={13} />
              <span className="hidden sm:inline">Sort</span>
              <ChevronDown size={13} className={`transition-transform ${sortOpen ? "rotate-180" : ""}`} />
            </button>
            {sortOpen && (
              <div className="absolute top-full right-0 mt-2 bg-cream border border-sectionbreak rounded-2xl shadow-lg py-2 min-w-[200px] z-20">
                {SORT_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => { setSort(o.id); setSortOpen(false); }}
                    className={`block w-full text-left px-5 py-2.5 text-sm hover:bg-sectionbreak/50 ${sort === o.id ? "text-blush" : "text-plum/80"}`}
                    data-testid={`sort-${o.id}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product grid */}
      <section className="py-12 lg:py-16" style={{ background: "#FDF6F0" }}>
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          {loading && products.length === 0 ? (
            <>
              <p className="mb-6 text-[12px] text-plum/60 tracking-wider uppercase">Loading pieces…</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-serif text-2xl text-plum italic mb-2">Nothing here yet</p>
              <p className="text-plum/60 text-sm">Try clearing filters to see all our pieces.</p>
            </div>
          ) : (
            <>
              <p className="mb-6 text-[12px] text-plum/60 tracking-wider uppercase">{products.length} pieces</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ShopPage;
